import {inject, Injectable, signal} from '@angular/core';
import {Router} from "@angular/router";
import {AuthConfig, OAuthService} from "angular-oauth2-oidc";
import {environment} from "@environments/environment";
import {ProfileClaims} from "@core/models/profile-claims";
import {LoginType} from '@core/enums/login-type.enum';
import {LoginExternoService} from '@core/services/login-externo.service';
import {Observable, of, shareReplay, switchMap, tap} from 'rxjs';
import {JwtHelperService} from '@auth0/angular-jwt';
import {UsuarioLogado} from '@core/models/usuario-logado';
import {UsuariosInternosService} from '@core/services/usuarios-internos.service';


@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly STORAGE_KEY_LOGIN_TYPE = 'loginType';
  private readonly STORAGE_KEY_TOKEN = 'token';
  private readonly usuarioInternoService = inject(UsuariosInternosService);

  private readonly _usuarioLogado = signal<UsuarioLogado | null>(null);
  readonly usuarioLogado = this._usuarioLogado.asReadonly();
  private _usuarioLogado$: Observable<UsuarioLogado> | null = null;

  constructor(
    private readonly oauthService: OAuthService,
    private readonly router: Router,
    private readonly usuarioExternoService: LoginExternoService,
  ) {
    const authConfig: AuthConfig = {
      issuer: environment.msal.issuer,
      redirectUri: environment.msal.redirectUri,
      clientId: environment.msal.clientId,
      responseType: 'code',
      scope: 'openid profile email user.read',
      showDebugInformation: false,
      strictDiscoveryDocumentValidation: false,
      requireHttps: environment.msal.requireHttps,
    };

    this.oauthService.configure(authConfig);
  }

  handleLoginRedirection() {
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      if (this.oauthService.hasValidAccessToken()) {
        this.initUsuarioLogado().subscribe(usuario => {
          if (usuario?.ativo) {
            this.router.navigate(['/']).then();
          } else {
            this.router.navigate(['/auth/no-permission']).then();
          }
        });
      } else {
        this.router.navigate(['/auth/login']).then();
      }
    });
  }

  initUsuarioLogado(): Observable<UsuarioLogado> {
    return this.usuarioInternoService.getUsuarioLogado().pipe(
      tap(usuario => this._usuarioLogado.set(usuario))
    );
  }

  getUsuarioLogado(): Observable<UsuarioLogado> {
    const current = this._usuarioLogado();
    if (current) {
      return of(current);
    }
    if (!this._usuarioLogado$) {
      this._usuarioLogado$ = this.initUsuarioLogado().pipe(shareReplay(1));
    }
    return this._usuarioLogado$;
  }

  login() {
    this.setStorageLoginType(LoginType.INTERNO);
    this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
      this.oauthService.initCodeFlow();
    });
  }

  logout() {
    this._usuarioLogado.set(null);
    this._usuarioLogado$ = null;

    if (this.getStorageLoginType() === LoginType.CONCESSIONARIA) {
      localStorage.removeItem(this.STORAGE_KEY_LOGIN_TYPE);
      localStorage.removeItem(this.STORAGE_KEY_TOKEN);
      this.router.navigate(['/auth/login']).then();
      return;
    }

    if (this.getStorageLoginType() === LoginType.INTERNO) {
      this.oauthService.loadDiscoveryDocumentAndTryLogin().then(() => {
        this.oauthService.logOut();
      });
    }
  }

  getProfileClaims(): ProfileClaims | null {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      const user = this._usuarioLogado();
      if (user) {
        return {
          name: user['nome'],
          email: user['username'],
          picture: '',
        };
      }
      return null;
    }

    return {
      name: claims['nome'],
      email: claims['email'],
      picture: claims['picture'],
    };
  }

  isAuthenticated(): boolean {
    if (this.getStorageLoginType() === LoginType.CONCESSIONARIA) {
      return this.isStorageTokenValid();
    }

    return this.oauthService.hasValidIdToken();
  }

  getIdToken() {
    return this.oauthService.getIdToken();
  }

  loginWithConcessionaria(email: string, token: string) {
    this.setStorageLoginType(LoginType.CONCESSIONARIA);

    return this.usuarioExternoService.validateToken(email, token).pipe(
      tap(requestToken => {
        this.setStorageToken(requestToken.token);
      }),
      switchMap(() => this.initUsuarioLogado()),
      tap(usuario => {
        if (usuario?.ativo) {
          this.router.navigate(['/']).then();
        } else {
          this.router.navigate(['/auth/no-permission']).then();
        }
      })
    );
  }

  isStorageTokenValid(): boolean {
    const token = this.getStorageToken();
    if (!token) return false;
    const jwtHelper = new JwtHelperService();
    return !jwtHelper.isTokenExpired(token);
  }

  getToken(): string {
    if (this.getStorageLoginType() === LoginType.INTERNO) {
      return this.getIdToken() || '';
    }
    return this.getStorageToken() || '';
  }

  setStorageLoginType(loginType: LoginType) {
    localStorage.setItem(this.STORAGE_KEY_LOGIN_TYPE, loginType);
  }

  getStorageLoginType(): LoginType | null {
    const loginType = localStorage.getItem(this.STORAGE_KEY_LOGIN_TYPE);
    return loginType ? (loginType as LoginType) : null;
  }

  setStorageToken(token: string) {
    localStorage.setItem(this.STORAGE_KEY_TOKEN, token);
  }

  getStorageToken(): string | null {
    return localStorage.getItem(this.STORAGE_KEY_TOKEN);
  }
}
